import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { DropdownMenuSubTriggerProps } from '@radix-ui/react-dropdown-menu';
import { Slot } from '@radix-ui/react-slot';
import { cn, cva } from '@thinkalpha/ui-core/utils';
import type { VariantProps } from '@thinkalpha/ui-core/utils';
import { forwardRef } from 'react';
import type { ReactNode } from 'react';
import { DropdownMenuItem, DropdownMenuSubTrigger } from 'src/components/ui';
import { Button } from 'src/components/ui/Buttons/button';
import type { ButtonProps } from 'src/components/ui/Buttons/button';

interface WidgetHeaderItemProps extends ButtonProps {}

export const WidgetHeaderItem = forwardRef<HTMLButtonElement, WidgetHeaderItemProps>(
    ({ children, className, variant = 'secondary', ...props }, ref) => {
        return (
            <Button
                ref={ref}
                variant={variant}
                className={cn('group/WidgetHeaderItem h-4 cursor-pointer px-1 py-0 text-sm', className)}
                {...props}
            >
                {children}
            </Button>
        );
    },
);

WidgetHeaderItem.displayName = 'WidgetHeaderItem';

const newWidgetHeaderItemVariants = cva(
    'group/WidgetHeaderItem flex h-6 w-auto cursor-pointer items-center justify-center gap-1 rounded-xs px-1 text-sm',
    {
        variants: {
            variant: {
                hover: 'transition-colors hover:bg-gray-700',
                'no-hover': '',
            },
        },
        defaultVariants: {
            variant: 'hover',
        },
    },
);

export type NewWidgetHeaderItemVariants = VariantProps<typeof newWidgetHeaderItemVariants>;

interface NewWidgetHeaderItemProps extends React.ButtonHTMLAttributes<HTMLDivElement>, NewWidgetHeaderItemVariants {
    asChild?: boolean;
}
export const NewWidgetHeaderItem = forwardRef<HTMLDivElement, NewWidgetHeaderItemProps>(
    ({ children, className, variant = 'hover', asChild, ...props }, ref) => {
        const Comp = asChild ? Slot : 'div';
        return (
            <Comp
                role="button"
                ref={ref}
                className={cn(newWidgetHeaderItemVariants({ variant }), className)}
                {...props}
            >
                {children}
            </Comp>
        );
    },
);

NewWidgetHeaderItem.displayName = 'NewWidgetHeaderItem';

/**
 * Reusable overflow sub-menu trigger component for consistent styling
 * in widget header overflow dropdown menus.
 */
interface WidgetHeaderOverflowSubMenuTriggerProps extends Omit<
    DropdownMenuSubTriggerProps,
    'chevronSide' | 'className'
> {
    className?: string;
}

export const WidgetHeaderOverflowSubMenuTrigger = forwardRef<
    React.ElementRef<typeof DropdownMenuSubTrigger>,
    WidgetHeaderOverflowSubMenuTriggerProps
>(({ children, className, ...props }, ref) => (
    <DropdownMenuSubTrigger
        ref={ref}
        chevronSide="right"
        className={cn('flex items-center gap-2 text-gray-300', className)}
        {...props}
    >
        {children}
    </DropdownMenuSubTrigger>
));

WidgetHeaderOverflowSubMenuTrigger.displayName = 'WidgetHeaderOverflowSubMenuTrigger';

/**
 * Reusable overflow checkbox item component for consistent styling
 * in widget header overflow dropdown menus for toggle/checkbox state items.
 */
interface WidgetHeaderOverflowCheckboxItemProps {
    checked: boolean;
    onCheckedChange: (checked: boolean) => void;
    children: ReactNode;
    className?: string;
}

export const WidgetHeaderOverflowCheckboxItem = ({
    checked,
    onCheckedChange,
    children,
    className,
}: WidgetHeaderOverflowCheckboxItemProps) => (
    <NewWidgetHeaderItem asChild>
        <DropdownMenuItem
            className={cn('flex items-center justify-between gap-2 text-gray-300', className)}
            onSelect={(e) => {
                e.preventDefault();
                onCheckedChange(!checked);
            }}
        >
            <div className="flex items-center gap-2">{children}</div>
            {checked && <FontAwesomeIcon icon={faCheck} className="pr-1" />}
        </DropdownMenuItem>
    </NewWidgetHeaderItem>
);
